import { useScrollToTop } from "@/hooks/useScrollToTop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ui/error-boundary";
import { StickyContact } from "@/components/StickyContact";
import { AuthProvider } from "@/hooks/useAuth";
import { I18nProvider } from "@/i18n";
import Index from "./pages/Index";
import About from "./pages/About";
import Contact from "./pages/Contact";
import SmartToilets from "./pages/SmartToilets";
import ProductReference from "./pages/ProductReference";
import Blog from "./pages/Blog";
import Business from "./pages/Business";
import Technology from "./pages/Technology";

// Products - Core lineup
import REM7 from "./pages/products/REM7";
import Luxa from "./pages/products/Luxa";
import Aria from "./pages/products/Aria";

// Products - REM7 Variants (renamed from old product names)
import Luna from "./pages/products/Luna";
import Luma from "./pages/products/Luma";
import Nova from "./pages/products/Nova";
import Aurelius from "./pages/products/Aurelius";
import Aurelux from "./pages/products/Aurelux";
import Vena from "./pages/products/Vena";
import U1 from "./pages/products/U1";
import K2 from "./pages/products/K2";
import S1 from "./pages/products/S1";
import M3 from "./pages/products/M3";
import L1 from "./pages/products/L1";

// Compare pages
import K2vsS1 from "./pages/compare/K2vsS1";
import M3vsS1vsK2 from "./pages/compare/M3vsS1vsK2";
import L1vsM3vsLuna from "./pages/compare/L1vsM3vsLuna";
import Compare from "./pages/Compare";
import Collection from "./pages/Collection";
import TradeBrochure from "./pages/TradeBrochure";
import SmartToiletCost from "./pages/SmartToiletCost";

// Blog posts
import LuxurySmartToiletGuide from "./pages/blog/LuxurySmartToiletGuide";
import SmartToiletVsBidet from "./pages/blog/SmartToiletVsBidet";
import SmartToiletTechnology2025 from "./pages/blog/SmartToiletTechnology2025";
import AureumVIICollection from "./pages/blog/AureumVIICollection";
import LuxaVsAurelux from "./pages/blog/LuxaVsAurelux";
import LunaM9Review from "./pages/blog/LunaM9Review";
import NovaFamilyFriendly from "./pages/blog/NovaFamilyFriendly";
import AriaH60EcoLuxury from "./pages/blog/AriaH60EcoLuxury";
import VenaM80FBidetExperience from "./pages/blog/VenaM80FBidetExperience";
import LuxuryBathroomIntelligentFeatures from "./pages/blog/LuxuryBathroomIntelligentFeatures";
import HealthBenefitsSmartToilets from "./pages/blog/HealthBenefitsSmartToilets";
import SmartToiletInstallationGuide from "./pages/blog/SmartToiletInstallationGuide";
import ComparingSmartToiletBrands from "./pages/blog/ComparingSmartToiletBrands";
import PregnancyComfortSmartToilets from "./pages/blog/PregnancyComfortSmartToilets";
import PostpartumRecoverySmartToilet from "./pages/blog/PostpartumRecoverySmartToilet";
import PregnancyBathroomSafetyGuide from "./pages/blog/PregnancyBathroomSafetyGuide";
import ExpectingMothersComfortGuide from "./pages/blog/ExpectingMothersComfortGuide";
import HomeRenovationGuide from "./pages/blog/HomeRenovationGuide";
import BathroomRemodelGuide from "./pages/blog/BathroomRemodelGuide";
import AffordableSmartToilets from "./pages/blog/AffordableSmartToilets";
import SmartToiletReviews2025 from "./pages/blog/SmartToiletReviews2025";
import WaterSavingSmartToilets from "./pages/blog/WaterSavingSmartToilets";
import WhatIsSmartToilet from "./pages/blog/WhatIsSmartToilet";
import BidetToiletWomenSwitch from "./pages/blog/BidetToiletWomenSwitch";
import WomenBathroomComfort from "./pages/blog/WomenBathroomComfort";
import SpaLikeBathroomDesign from "./pages/blog/SpaLikeBathroomDesign";
import CleaningStressToLuxury from "./pages/blog/CleaningStressToLuxury";
import PregnancySmartToiletComfort from "./pages/blog/PregnancySmartToiletComfort";
import EuropeanSmartToilets2025 from "./pages/blog/EuropeanSmartToilets2025";
import MoldFreeBathroomDesign from "./pages/blog/MoldFreeBathroomDesign";
import SmartToiletUltimateGuide from "./pages/blog/SmartToiletUltimateGuide";
import BathroomFixturesGuide2025 from "./pages/blog/BathroomFixturesGuide2025";
import SquareVsEggSmartToiletDesign from "./pages/blog/SquareVsEggSmartToiletDesign";
import SmartToiletsForSeniors from "./pages/blog/SmartToiletsForSeniors";
import HemorrhoidReliefSmartToilets from "./pages/blog/HemorrhoidReliefSmartToilets";
import PostpartumRecoveryBathroom from "./pages/blog/PostpartumRecoveryBathroom";
import BidetHealthBenefits from "./pages/blog/BidetHealthBenefits";
import SmartToiletsAccessibility from "./pages/blog/SmartToiletsAccessibility";
import DigestiveHealthSmartToilets from "./pages/blog/DigestiveHealthSmartToilets";
import UTIPreventionBidet from "./pages/blog/UTIPreventionBidet";
import EcoFriendlySmartToilet from "./pages/blog/EcoFriendlySmartToilet";
import SkinHealthBidet from "./pages/blog/SkinHealthBidet";
import NighttimeBathroomSafety from "./pages/blog/NighttimeBathroomSafety";
import Rem7VsTotoVsGeberit from "./pages/blog/Rem7VsTotoVsGeberit";
import TanklessSmartToiletEurope from "./pages/blog/TanklessSmartToiletEurope";
import GeberitAquaCleanAlternatives from "./pages/blog/GeberitAquaCleanAlternatives";
import Rem7VsDuravitSensowash from "./pages/blog/Rem7VsDuravitSensowash";
import Rem7VsKohlerVeil from "./pages/blog/Rem7VsKohlerVeil";
import SmartToiletInstallationHotels from "./pages/blog/SmartToiletInstallationHotels";
import SpecifyingSmartToiletsMultiUnit from "./pages/blog/SpecifyingSmartToiletsMultiUnit";
import SmartToiletRoiBoutiqueHotels from "./pages/blog/SmartToiletRoiBoutiqueHotels";
import SmartToiletSpecSheetGuide from "./pages/blog/SmartToiletSpecSheetGuide";
import InodoroInteligenteGuia from "./pages/blog/InodoroInteligenteGuia";
import JapaneseToiletVsEuropeanSmartToilet from "./pages/blog/JapaneseToiletVsEuropeanSmartToilet";

// Other pages
import NotFound from "./pages/NotFound";
import FAQPage from "./pages/FAQ";
import EuropeanMarkets from "./pages/EuropeanMarkets";
import Germany from "./pages/markets/Germany";
import UK from "./pages/markets/UK";
import France from "./pages/markets/France";
import Italy from "./pages/markets/Italy";
import Spain from "./pages/markets/Spain";
import USA from "./pages/markets/USA";
import WorkflowAssistantPage from "./pages/WorkflowAssistantPage";
import ClientHub from "./pages/ClientHub";
import Sustainability from "./pages/Sustainability";
import BathSanitary from "./pages/BathSanitary";
import AriaSculpt from "./pages/bath/AriaSculpt";
import AriaMonolith from "./pages/bath/AriaMonolith";
import AriaDiamond from "./pages/bath/AriaDiamond";
import VenaBasin from "./pages/bath/VenaBasin";
import VenaVanity from "./pages/bath/VenaVanity";
import Rem7Whirlpool from "./pages/bath/Rem7Whirlpool";
import Rem7WhirlpoolCompact from "./pages/bath/Rem7WhirlpoolCompact";
import Rem7WhirlpoolSurf from "./pages/bath/Rem7WhirlpoolSurf";
import Rem7WhirlpoolDeluxe from "./pages/bath/Rem7WhirlpoolDeluxe";
import VelaFreestanding from "./pages/bath/VelaFreestanding";
import BathImageStatus from "./pages/bath/BathImageStatus";
import ImageAudit from "./pages/ImageAudit";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import EmailTest from "./pages/EmailTest";
import AgentLogin from "./pages/AgentLogin";
import AgentDashboard from "./pages/AgentDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ReturnPolicy from "./pages/ReturnPolicy";
import TermsConditions from "./pages/TermsConditions";
import ShippingPolicy from "./pages/ShippingPolicy";
import { ClientAssistant } from "./components/ClientAssistant";
import ConversionTracking from "./components/ConversionTracking";
import CookieConsent from "./components/CookieConsent";
import SEODashboard from "./pages/SEODashboard";
import SitemapSubmission from "./pages/SitemapSubmission";
import BacklinkDashboard from "./pages/BacklinkDashboard";
import OAuthConsent from "./pages/OAuthConsent";

const queryClient = new QueryClient();

const ScrollToTop = () => {
  useScrollToTop();
  return null;
};

const AppContent = () => (
  <>
    <ScrollToTop />
    <Routes>
    {/* Redirect root to default language */}
    <Route path="/" element={<Navigate to="/en" replace />} />

    {/* OAuth consent (no language prefix; managed OAuth server redirects here) */}
    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />


    
    {/* Language-prefixed routes */}
    <Route path="/:lang" element={<Index />} />
    <Route path="/:lang/about" element={<About />} />
    <Route path="/:lang/contact" element={<Contact />} />
    <Route path="/:lang/client-hub" element={<ClientHub />} />
    <Route path="/:lang/smart-toilets" element={<SmartToilets />} />
    <Route path="/:lang/product-reference" element={<ProductReference />} />
    <Route path="/:lang/blog" element={<Blog />} />
    <Route path="/:lang/faq" element={<FAQPage />} />
    <Route path="/:lang/business" element={<Business />} />
    <Route path="/:lang/collection" element={<Collection />} />
    <Route path="/:lang/trade-brochure" element={<TradeBrochure />} />
    <Route path="/:lang/sustainability" element={<Sustainability />} />
    <Route path="/:lang/technology" element={<Technology />} />
    <Route path="/:lang/bath-sanitary" element={<BathSanitary />} />
    <Route path="/:lang/bath-sanitary/aria-sculpt" element={<AriaSculpt />} />
    <Route path="/:lang/bath-sanitary/aria-monolith" element={<AriaMonolith />} />
    <Route path="/:lang/bath-sanitary/aria-diamond" element={<AriaDiamond />} />
    <Route path="/:lang/bath-sanitary/vena-basin" element={<VenaBasin />} />
    <Route path="/:lang/bath-sanitary/vena-vanity" element={<VenaVanity />} />
    <Route path="/:lang/bath-sanitary/rem7-whirlpool" element={<Rem7Whirlpool />} />
    <Route path="/:lang/bath-sanitary/rem7-whirlpool-compact" element={<Rem7WhirlpoolCompact />} />
    <Route path="/:lang/bath-sanitary/rem7-whirlpool-surf" element={<Rem7WhirlpoolSurf />} />
    <Route path="/:lang/bath-sanitary/rem7-whirlpool-deluxe" element={<Rem7WhirlpoolDeluxe />} />
    <Route path="/:lang/bath-sanitary/vela-freestanding" element={<VelaFreestanding />} />
    <Route path="/:lang/bath-sanitary/image-status" element={<BathImageStatus />} />
    <Route path="/:lang/image-audit" element={<ImageAudit />} />
    <Route path="/:lang/auth" element={<Auth />} />
    <Route path="/:lang/admin" element={<Admin />} />
    <Route path="/:lang/admin/email-test" element={<EmailTest />} />
    <Route path="/:lang/agent-login" element={<AgentLogin />} />
    <Route path="/:lang/agent-dashboard" element={<AgentDashboard />} />
    
    {/* Blog posts */}
    <Route path="/:lang/blog/luxury-smart-toilet-guide" element={<LuxurySmartToiletGuide />} />
    <Route path="/:lang/blog/smart-toilet-vs-bidet" element={<SmartToiletVsBidet />} />
    <Route path="/:lang/blog/smart-toilet-technology-2025" element={<SmartToiletTechnology2025 />} />
    <Route path="/:lang/blog/aureum-vii-collection" element={<AureumVIICollection />} />
    <Route path="/:lang/blog/luxa-vs-aurelux" element={<LuxaVsAurelux />} />
    <Route path="/:lang/blog/luna-m9-review" element={<LunaM9Review />} />
    <Route path="/:lang/blog/nova-family-friendly" element={<NovaFamilyFriendly />} />
    <Route path="/:lang/blog/aria-h60-eco-luxury" element={<AriaH60EcoLuxury />} />
    <Route path="/:lang/blog/vena-m80f-bidet-experience" element={<VenaM80FBidetExperience />} />
    <Route path="/:lang/blog/luxury-bathroom-intelligent-features" element={<LuxuryBathroomIntelligentFeatures />} />
    <Route path="/:lang/blog/health-benefits-smart-toilets" element={<HealthBenefitsSmartToilets />} />
    <Route path="/:lang/blog/smart-toilet-installation-guide" element={<SmartToiletInstallationGuide />} />
    <Route path="/:lang/blog/comparing-smart-toilet-brands" element={<ComparingSmartToiletBrands />} />
    <Route path="/:lang/blog/pregnancy-comfort-smart-toilets" element={<PregnancyComfortSmartToilets />} />
    <Route path="/:lang/blog/postpartum-recovery-smart-toilet" element={<PostpartumRecoverySmartToilet />} />
    <Route path="/:lang/blog/pregnancy-bathroom-safety-guide" element={<PregnancyBathroomSafetyGuide />} />
    <Route path="/:lang/blog/expecting-mothers-comfort-guide" element={<ExpectingMothersComfortGuide />} />
    <Route path="/:lang/blog/home-renovation-guide" element={<HomeRenovationGuide />} />
    <Route path="/:lang/blog/bathroom-remodel-guide" element={<BathroomRemodelGuide />} />
    <Route path="/:lang/blog/affordable-smart-toilets" element={<AffordableSmartToilets />} />
    <Route path="/:lang/blog/smart-toilet-reviews-2025" element={<SmartToiletReviews2025 />} />
    <Route path="/:lang/blog/water-saving-smart-toilets" element={<WaterSavingSmartToilets />} />
    <Route path="/:lang/blog/what-is-smart-toilet" element={<WhatIsSmartToilet />} />
    <Route path="/:lang/blog/bidet-toilet-women-switch" element={<BidetToiletWomenSwitch />} />
    <Route path="/:lang/blog/women-bathroom-comfort" element={<WomenBathroomComfort />} />
    <Route path="/:lang/blog/spa-like-bathroom-design" element={<SpaLikeBathroomDesign />} />
    <Route path="/:lang/blog/cleaning-stress-to-luxury" element={<CleaningStressToLuxury />} />
    <Route path="/:lang/blog/pregnancy-smart-toilet-comfort" element={<PregnancySmartToiletComfort />} />
    <Route path="/:lang/blog/european-smart-toilets-2025" element={<EuropeanSmartToilets2025 />} />
    <Route path="/:lang/blog/mold-free-bathroom-design" element={<MoldFreeBathroomDesign />} />
    <Route path="/:lang/blog/smart-toilet-bidet-guide" element={<SmartToiletUltimateGuide />} />
    <Route path="/:lang/blog/bathroom-fixtures-guide-2025" element={<BathroomFixturesGuide2025 />} />
    <Route path="/:lang/blog/square-vs-egg-smart-toilet-design" element={<SquareVsEggSmartToiletDesign />} />
    <Route path="/:lang/blog/smart-toilets-for-seniors" element={<SmartToiletsForSeniors />} />
    <Route path="/:lang/blog/hemorrhoid-relief-smart-toilets" element={<HemorrhoidReliefSmartToilets />} />
    <Route path="/:lang/blog/postpartum-recovery-bathroom" element={<PostpartumRecoveryBathroom />} />
    <Route path="/:lang/blog/bidet-health-benefits" element={<BidetHealthBenefits />} />
    <Route path="/:lang/blog/smart-toilets-accessibility" element={<SmartToiletsAccessibility />} />
    <Route path="/:lang/blog/digestive-health-smart-toilets" element={<DigestiveHealthSmartToilets />} />
    <Route path="/:lang/blog/uti-prevention-bidet" element={<UTIPreventionBidet />} />
    <Route path="/:lang/blog/eco-friendly-smart-toilet" element={<EcoFriendlySmartToilet />} />
    <Route path="/:lang/blog/skin-health-bidet" element={<SkinHealthBidet />} />
    <Route path="/:lang/blog/nighttime-bathroom-safety" element={<NighttimeBathroomSafety />} />
    <Route path="/:lang/blog/rem7-vs-toto-neorest-vs-geberit-aquaclean" element={<Rem7VsTotoVsGeberit />} />
    <Route path="/:lang/blog/tankless-smart-toilet-europe" element={<TanklessSmartToiletEurope />} />
    <Route path="/:lang/blog/geberit-aquaclean-alternatives" element={<GeberitAquaCleanAlternatives />} />
    <Route path="/:lang/blog/rem7-vs-duravit-sensowash" element={<Rem7VsDuravitSensowash />} />
    <Route path="/:lang/blog/rem7-vs-kohler-veil" element={<Rem7VsKohlerVeil />} />
    <Route path="/:lang/blog/smart-toilet-installation-hotels" element={<SmartToiletInstallationHotels />} />
    <Route path="/:lang/blog/specifying-smart-toilets-multi-unit-residential" element={<SpecifyingSmartToiletsMultiUnit />} />
    <Route path="/:lang/blog/smart-toilet-roi-boutique-hotels" element={<SmartToiletRoiBoutiqueHotels />} />
    <Route path="/:lang/blog/smart-toilet-spec-sheet-guide" element={<SmartToiletSpecSheetGuide />} />
    <Route path="/:lang/blog/inodoro-inteligente-guia" element={<InodoroInteligenteGuia />} />
    <Route path="/:lang/blog/japanese-toilet-vs-luxury-smart-toilet" element={<JapaneseToiletVsEuropeanSmartToilet />} />
    
    {/* Core Products - REM7, Luxa, Aria */}
    <Route path="/:lang/products/rem7" element={<REM7 />} />
    <Route path="/:lang/products/luxa" element={<Luxa />} />
    <Route path="/:lang/products/aria" element={<Aria />} />
    
    {/* REM7 Variants (legacy product routes maintained for SEO) */}
    <Route path="/:lang/products/luna" element={<Luna />} />
    <Route path="/:lang/products/luma" element={<Luma />} />
    <Route path="/:lang/products/nova" element={<Nova />} />
    <Route path="/:lang/products/aurelius" element={<Aurelius />} />
    <Route path="/:lang/products/aurelux" element={<Aurelux />} />
    <Route path="/:lang/products/vena" element={<Vena />} />
    <Route path="/:lang/products/u1" element={<U1 />} />
    <Route path="/:lang/products/k2" element={<K2 />} />
    <Route path="/:lang/products/s1" element={<S1 />} />
    <Route path="/:lang/products/m3" element={<M3 />} />
    <Route path="/:lang/products/l1" element={<L1 />} />
    
    {/* Compare */}
    <Route path="/:lang/compare/k2-vs-s1" element={<K2vsS1 />} />
    <Route path="/:lang/compare/m3-vs-s1-vs-k2" element={<M3vsS1vsK2 />} />
    <Route path="/:lang/compare/l1-vs-m3-vs-luna" element={<L1vsM3vsLuna />} />
    <Route path="/:lang/compare" element={<Compare />} />
    <Route path="/:lang/smart-toilet-cost" element={<SmartToiletCost />} />
    
    {/* Markets */}
    <Route path="/:lang/european-markets" element={<EuropeanMarkets />} />
    <Route path="/:lang/markets/germany" element={<Germany />} />
    <Route path="/:lang/markets/uk" element={<UK />} />
    <Route path="/:lang/markets/france" element={<France />} />
    <Route path="/:lang/markets/italy" element={<Italy />} />
    <Route path="/:lang/markets/spain" element={<Spain />} />
    <Route path="/:lang/markets/usa" element={<USA />} />
    
    {/* Legal */}
    <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/:lang/return-policy" element={<ReturnPolicy />} />
    <Route path="/:lang/terms-conditions" element={<TermsConditions />} />
    <Route path="/:lang/shipping-policy" element={<ShippingPolicy />} />
    
    {/* Other */}
    <Route path="/:lang/workflow-assistant" element={<WorkflowAssistantPage />} />
    <Route path="/:lang/seo-dashboard" element={<SEODashboard />} />
    <Route path="/:lang/sitemap-submission" element={<SitemapSubmission />} />
    <Route path="/:lang/backlink-dashboard" element={<BacklinkDashboard />} />
    
    {/* Legacy routes (redirect to language-prefixed version) */}
    <Route path="/about" element={<Navigate to="/en/about" replace />} />
    <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
    <Route path="/smart-toilets" element={<Navigate to="/en/smart-toilets" replace />} />
    <Route path="/smart-toilet-cost" element={<Navigate to="/en/smart-toilet-cost" replace />} />
    <Route path="/blog" element={<Navigate to="/en/blog" replace />} />
    <Route path="/technology" element={<Navigate to="/en/technology" replace />} />
    <Route path="/agent-login" element={<Navigate to="/en/agent-login" replace />} />
    <Route path="/agent-dashboard" element={<Navigate to="/en/agent-dashboard" replace />} />
    <Route path="/products/*" element={<Navigate to={`/en${window.location.pathname}`} replace />} />
    
    {/* Redirect old /rem7 to /products/rem7 */}
    <Route path="/:lang/rem7" element={<Navigate to="/en/products/rem7" replace />} />
    <Route path="/rem7" element={<Navigate to="/en/products/rem7" replace />} />
    
    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
    <Route path="/:lang/*" element={<NotFound />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
  </>
);

const App = () => {
  return (
    <ErrorBoundary showDetails={false}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <I18nProvider>
              <AuthProvider>
                <ConversionTracking />
                <AppContent />
                <ClientAssistant />
                <StickyContact />
                <CookieConsent />
                <Toaster />
                <Sonner />
              </AuthProvider>
            </I18nProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
